import * as clientDb from "../db/clientDb";
import { FALLBACK_PROMPTS } from "../fallbackPrompts.defaults";
import type { DashboardGreeting, DashboardVital, RecipeSuggestion } from "../types/dashboard";

type TimeSlot = "morning" | "afternoon" | "evening";

interface ExpiringItem {
    id: number;
    name: string;
    quantity: number;
    expiry_date: string | null;
    daysLeft: number | null;
}

export interface GreetingInput {
    slot: TimeSlot;
    totalItems: number;
    expiringItems: ExpiringItem[];
    randomItems: Array<{ id: number; name: string }>;
    allItems: clientDb.LocalItem[];
}

export interface VitalInput {
    slot: TimeSlot;
    expiringItems: ExpiringItem[];
}

interface GreetingJson {
    headline: string;
    line: string;
    references?: { slot?: string; totalItems?: number; itemIds?: number[] };
}

interface VitalJson {
    value: number;
    label: string;
    references?: { slot?: string; totalItems?: number; itemIds?: number[] };
}

interface RecipeJson {
    dish: string;
    ingredients: Array<string | { label?: string; itemId?: number }>;
    steps: string[];
    summary: string;
    references?: unknown;
}

export interface RecipeCandidate extends clientDb.LocalItem {
    daysLeft: number | null;
}

function renderPrompt(relativePath: string, vars: Record<string, string | number>): string {
    let text = FALLBACK_PROMPTS[relativePath];
    if (text === undefined) {
        throw new Error(`Missing fallback prompt: ${relativePath}`);
    }
    for (const [key, value] of Object.entries(vars)) {
        text = text.replaceAll(`{{${key}}}`, String(value));
    }
    return text;
}

function loadFallbackJson<T>(relativePath: string, vars: Record<string, string | number>): T {
    return JSON.parse(renderPrompt(relativePath, vars)) as T;
}

function getToday(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
}

function daysUntilExpiry(expiryDate: string, today: Date): number | null {
    const expiry = new Date(`${expiryDate}T00:00:00`);
    const days = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return Number.isNaN(days) ? null : days;
}

export function getTimeSlot(date: Date = new Date()): TimeSlot {
    const h = date.getHours();
    if (h >= 6 && h < 12) return "morning";
    if (h >= 12 && h < 17) return "afternoon";
    return "evening";
}

function getSlotLabel(slot: TimeSlot): string {
    return slot;
}

async function buildExpiringItems(): Promise<ExpiringItem[]> {
    const items = await clientDb.listItems();
    const today = getToday();
    return items
        .filter((item) => item.quantity > 0 && item.expiry_date)
        .map((item) => ({
            id: item.id,
            name: item.name,
            quantity: item.quantity,
            expiry_date: item.expiry_date,
            daysLeft: item.expiry_date ? daysUntilExpiry(item.expiry_date, today) : null,
        }))
        .filter((item) => item.daysLeft !== null && item.daysLeft <= 7)
        .sort((a, b) => (a.daysLeft ?? Infinity) - (b.daysLeft ?? Infinity));
}

export async function buildGreetingInput(now: Date): Promise<GreetingInput & { slotKey: string }> {
    const slot = getTimeSlot(now);
    const [allItems, expiringItems] = await Promise.all([clientDb.listItems(), buildExpiringItems()]);
    const totalItems = allItems.filter((item) => item.quantity > 0).reduce((sum, item) => sum + item.quantity, 0);
    const randomItems = allItems
        .filter((item) => item.quantity > 0)
        .sort(() => Math.random() - 0.5)
        .slice(0, 10)
        .map((item) => ({ id: item.id, name: item.name }));
    return {
        slot,
        slotKey: getSlotLabel(slot),
        totalItems,
        expiringItems,
        randomItems,
        allItems,
    };
}

function jsonItemIdsField(ids: number[]): string {
    const valid = ids.filter((id) => id > 0);
    return valid.length ? `, "itemIds": [${valid.join(", ")}]` : "";
}

function formatFirstItemName(name: string): string {
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}

function loadGreetingFallback(path: string, vars: Record<string, string | number>): GreetingJson | null {
    try {
        const parsed = loadFallbackJson<GreetingJson>(path, vars);
        if (!parsed || typeof parsed.headline !== "string" || typeof parsed.line !== "string") {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

export function fallbackGreeting(input: GreetingInput): DashboardGreeting {
    const { slot, totalItems, expiringItems } = input;
    const urgent = expiringItems.filter((e) => (e.daysLeft ?? Infinity) <= 7);
    const first = expiringItems[0];
    const rottingPhrase = `${urgent.length} thing${urgent.length === 1 ? "" : "s"}`;

    if (slot === "morning") {
        if (urgent.length > 0) {
            const fb = loadGreetingFallback(
                "fallbacks/greeting-morning-rotting.txt",
                { rottingPhrase, itemIdsField: jsonItemIdsField(first ? [first.id] : []) },
            );
            if (fb) return { headline: fb.headline, line: fb.line };
        } else {
            const fb = loadGreetingFallback("fallbacks/greeting-morning-clean.txt", { totalItems });
            if (fb) return { headline: fb.headline, line: fb.line };
        }
    }

    if (slot === "afternoon") {
        if (first) {
            const fb = loadGreetingFallback(
                "fallbacks/greeting-afternoon-expiring.txt",
                { firstItem: formatFirstItemName(first.name), firstItemId: first.id },
            );
            if (fb) return { headline: fb.headline, line: fb.line };
        } else {
            const fb = loadGreetingFallback(
                "fallbacks/greeting-afternoon-clean.txt",
                { totalItems },
            );
            if (fb) return { headline: fb.headline, line: fb.line };
        }
    }

    if (first) {
        const fb = loadGreetingFallback(
            "fallbacks/greeting-evening-expiring.txt",
            { firstItem: formatFirstItemName(first.name), firstItemId: first.id },
        );
        if (fb) return { headline: fb.headline, line: fb.line };
    }

    const fb = loadGreetingFallback("fallbacks/greeting-evening-clean.txt", { totalItems });
    if (fb) return { headline: fb.headline, line: fb.line };

    return { headline: "Pantry Pal", line: "Your dashboard is ready." };
}

export async function getFallbackGreeting(now: Date): Promise<{ greeting: DashboardGreeting; greetingSlot: string }> {
    const input = await buildGreetingInput(now);
    return { greeting: fallbackGreeting(input), greetingSlot: input.slotKey };
}

function loadVitalFallback(path: string, vars: Record<string, string | number>): VitalJson | null {
    try {
        const parsed = loadFallbackJson<VitalJson>(path, vars);
        if (!parsed || typeof parsed.value !== "number" || typeof parsed.label !== "string") {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

export function fallbackVital(input: VitalInput): DashboardVital {
    const { slot, expiringItems } = input;
    const expired = expiringItems.filter((e) => (e.daysLeft ?? Infinity) < 0);
    const soon = expiringItems.filter((e) => {
        const d = e.daysLeft ?? Infinity;
        return d >= 0 && d <= 7;
    });

    if (expired.length > 0) {
        const fb = loadVitalFallback("fallbacks/vital-expired.txt", {
            count: expired.length,
            itemIds: expired.map((e) => e.id).join(", "),
        });
        if (fb) return { value: fb.value, label: fb.label };
    }

    if (soon.length > 0) {
        const fb = loadVitalFallback("fallbacks/vital-expiring.txt", {
            count: soon.length,
            itemIds: soon.map((e) => e.id).join(", "),
        });
        if (fb) return { value: fb.value, label: fb.label };
    }

    const slotMap: Record<TimeSlot, string> = {
        morning: "fallbacks/vital-morning.txt",
        afternoon: "fallbacks/vital-afternoon.txt",
        evening: "fallbacks/vital-evening.txt",
    };
    const fb = loadVitalFallback(slotMap[slot], {});
    if (fb) return { value: fb.value, label: fb.label };

    return { value: 0, label: "possible dinners" };
}

export async function getFallbackVital(now: Date): Promise<DashboardVital> {
    const slot = getTimeSlot(now);
    const expiringItems = await buildExpiringItems();
    return fallbackVital({ slot, expiringItems });
}

export async function getRecipeCandidates(limit = 100): Promise<RecipeCandidate[]> {
    const [items] = await Promise.all([clientDb.listItems()]);
    const today = getToday();
    return items
        .filter((item) => item.quantity > 0)
        .map((item) => ({
            ...item,
            daysLeft: item.expiry_date ? daysUntilExpiry(item.expiry_date, today) : null,
        }))
        .sort((a, b) => {
            if (a.daysLeft === null && b.daysLeft === null) return a.name.localeCompare(b.name);
            if (a.daysLeft === null) return 1;
            if (b.daysLeft === null) return -1;
            if (a.daysLeft !== b.daysLeft) return a.daysLeft - b.daysLeft;
            return a.name.localeCompare(b.name);
        })
        .slice(0, limit) as RecipeCandidate[];
}

function parseIngredientEntry(raw: unknown): { label: string; itemId?: number } | null {
    if (typeof raw === "string") {
        const label = raw.trim();
        return label ? { label } : null;
    }
    if (!raw || typeof raw !== "object") return null;
    const entry = raw as { label?: string; itemId?: number };
    const label = entry.label?.trim();
    if (!label) return null;
    const ingredient: { label: string; itemId?: number } = { label };
    if (typeof entry.itemId === "number" && entry.itemId > 0) {
        ingredient.itemId = entry.itemId;
    }
    return ingredient;
}

function normalizeRecipe(parsed: RecipeJson, candidates: RecipeCandidate[]): RecipeSuggestion {
    const candidateIds = new Set(candidates.map((c) => c.id));
    let ingredients = parsed.ingredients
        .map(parseIngredientEntry)
        .filter((i): i is { label: string; itemId?: number } => i !== null)
        .map((ing) => {
            if (ing.itemId !== undefined && !candidateIds.has(ing.itemId)) {
                return { label: ing.label };
            }
            return ing;
        });

    if (!ingredients.some((i) => i.itemId !== undefined)) {
        for (const c of candidates.slice(0, 2)) {
            ingredients.unshift({ label: c.name, itemId: c.id });
        }
    }

    return {
        dish: parsed.dish,
        ingredients,
        steps: parsed.steps.filter((s) => typeof s === "string").map((s) => (s as string).trim()),
        summary: parsed.summary,
    };
}

export function fallbackRecipe(candidates: RecipeCandidate[]): RecipeSuggestion | null {
    if (candidates.length === 0) {
        return null;
    }

    const targets = candidates.slice(0, 2);
    const names = targets.map((t) => t.name).join(" & ");
    const ingredientNames = targets.map((t) => t.name).join(" and ");
    const ingredients = [
        ...targets.map((t) => ({ label: t.name, itemId: t.id })),
        { label: "olive oil" },
        { label: "salt" },
        { label: "pepper" },
    ];
    const references = {
        pool: candidates.map((c) => ({ id: c.id, quantity: c.quantity, daysLeft: c.daysLeft })),
        items: targets.map((t) => ({ id: t.id, quantity: t.quantity, daysLeft: t.daysLeft })),
    };

    try {
        const parsed = loadFallbackJson<RecipeJson>("fallbacks/recipe.txt", {
            names,
            ingredients: ingredientNames,
            ingredientsJson: JSON.stringify(ingredients),
            referencesJson: JSON.stringify(references),
        });
        if (
            parsed &&
            typeof parsed.dish === "string" &&
            parsed.dish.length > 0 &&
            Array.isArray(parsed.ingredients) &&
            parsed.ingredients.length > 0 &&
            Array.isArray(parsed.steps) &&
            parsed.steps.length > 0 &&
            typeof parsed.summary === "string" &&
            parsed.summary.length > 0
        ) {
            return normalizeRecipe(parsed, candidates);
        }
    } catch {
        /* fall through to hardcoded fallback */
    }

    return {
        dish: `${names} Whatever Skillet`,
        ingredients,
        steps: [
            `Check that your ${ingredientNames} haven't given up on life yet.`,
            "Hot pan. Everything goes in before it turns. This is not MasterChef.",
            'Season it, eat it, tell the kids it\'s "rustic." You\'re welcome.',
        ],
        summary: "Not Pinterest-worthy. Not landfill. Exactly what I'd make on a Tuesday.",
    };
}

export async function getFallbackRecipe(): Promise<{ recipe: RecipeSuggestion | null; candidateIds: number[] }> {
    const candidates = await getRecipeCandidates(100);
    return { recipe: fallbackRecipe(candidates), candidateIds: candidates.map((c) => c.id) };
}
